/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import React, {useEffect, useMemo, useRef, useCallback, useState} from 'react'
import {useLocation, useParams, Redirect} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'

// Components
import {
    Box,
    Flex,
    SimpleGrid,
    Text,
    Stack,
    useDisclosure,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Container
} from '@chakra-ui/react'
import BackToTopLink from '../../components/back-to-top-link'
import CategoriesBreadcrumb from '../../components/breadcrumb/categories-breadcrumb'

// Project Components
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../components/product-tile'
import {getPathParamsSplit, removeSiteLocaleFromPath} from '../../utils/url'
import FiltersSection from './partials/filters-section'
import Refinements from './partials/refinements'
import EmptyResults from './partials/empty-results'
import PageHeader from './partials/page-header'
import CMSContent from '../../components/cms-content'
import ContentSlot from '../../components/content-slot'

// Hooks
import {useSearchParams} from '../../hooks'
import useEinstein from '../../hooks/use-einstein'
import useTrackViewList from '../../components/recommended-products/hooks/use-track-view-list'

// Others
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {trackSelectItem, trackEvent} from '../../tracking/analytics'

// Constants
import {
    DEFAULT_SEARCH_PARAMS,
    DEFAULT_CATEGORY_PARAMS,
    CATEGORY_BANNER_SLOT_ID,
    HIDDEN_REFINEMENTS,
    CATEGORY_BANNER_PROMO_TOP,
    CATEGORY_BANNER_PROMO_MIDDLE,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE
} from '../../constants'
import useNavigation from '../../hooks/use-navigation'
import LoadingSpinner from '../../components/loading-spinner'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import PaginationLegend from './partials/pagination-legend'
import PlpSubnavigation from './partials/subnavigation'
import Seo from '../../components/seo'
import useEffectEvent from '../../hooks/use-effect-event'
import HeaderContent from '../../components/header-content'
import {useCategory, useUrlMapping} from '@salesforce/commerce-sdk-react'
import {useProductInfinitySearch} from '../../hooks/use-product-infinity-search'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useSlugUrlRedirect from './hooks/use-slug-url-redirect'
import {isServer} from '../../utils/utils'
import useStructuredData from '../../hooks/use-structured-data'
import useConvertPageTags from '../../convert-api/hooks/use-convert-page-tags'
import PropTypes from 'prop-types'
import RecommendedProducts from '../../components/recommended-products'
import ModuleContainer from '../../leap/components/module-container'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']

/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const ProductList = ({categoryId: categoryIdOverride}) => {
    const {isOpen, onOpen: _onOpen, onClose} = useDisclosure()
    // capture the element that triggered the modal open to return focus to it
    // important for iOS accessibility, as the active element is the container for whatever reason
    const filterModalReturnRef = useRef(null)
    const onOpen = useCallback(
        (_value, _href, event) => {
            filterModalReturnRef.current = event?.currentTarget || event?.target
            return _onOpen()
        },
        [_onOpen]
    )
    const {formatMessage, locale} = useIntl()
    // use router navigation for query parameters update
    // noinspection JSDeprecatedSymbols
    const navigate = useNavigation()
    const params = useParams()
    const location = useLocation()
    const einstein = useEinstein()
    const isSearch = !categoryIdOverride && !params.categoryPath
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams(
        isSearch ? DEFAULT_SEARCH_PARAMS : DEFAULT_CATEGORY_PARAMS
    )
    const {searchQuery, forcelisting} = useMemo(() => {
        const urlParams = new URLSearchParams(location.search)
        return {
            searchQuery: urlParams.get('q'),
            forcelisting: Boolean(urlParams.get('forcelisting'))
        }
    }, [location.search])

    /**************** Page State ****************/
    const filtersRef = useRef(null)

    /**************** Query Actions ****************/
    const urlSegment = useMemo(
        () => removeSiteLocaleFromPath(location.pathname),
        [location.pathname]
    )
    const {error: urlMappingError, data: urlMapping} = useUrlMapping(
        {
            parameters: {
                urlSegment
            }
        },
        {
            enabled: !isSearch
        }
    )
    const categoryId = categoryIdOverride || urlMapping?.resourceId
    const {error: categoryError, data: category} = useCategory(
        {
            parameters: {
                id: categoryId,
                levels: 0
            }
        },
        {
            enabled: Boolean(categoryId)
        }
    )

    const productSearchParams = useMemo(() => {
        // _refine is an invalid param for useProductSearch, we don't want to pass it to API call
        const {_refine, ...restOfParams} = searchParams

        return {
            ...restOfParams,
            perPricebook: true,
            allVariationProperties: true,
            expand: [
                'promotions',
                'variations',
                'prices',
                'images',
                'availability',
                'custom_properties'
            ],
            select: '(**,hits.(**,representedProduct.(id)))',
            refine: [
                'available-on-web=1',
                ...(_refine || []),
                ...(categoryId ? [`cgid=${categoryId}`] : [])
            ]
        }
    }, [categoryId, searchParams])

    const {
        data: productSearchResult,
        fetchNextPage: _fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage
    } = useProductInfinitySearch(
        {
            parameters: productSearchParams
        },
        {
            enabled: isSearch || !!categoryId,
            keepPreviousData: true,
            select: (data) => {
                if (!data?.pages?.length) {
                    return null
                }

                return {
                    ...data.pages[0],
                    hits: data.pages
                        .map(({hits}) => hits)
                        .filter(Boolean)
                        .flat(),
                    refinements: data.pages[0].refinements?.filter(
                        ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
                    ),
                    // filter out refinements that are not allowed to be shown
                    ...(data.pages[0].selectedRefinements && {
                        selectedRefinements: Object.fromEntries(
                            Object.entries(data.pages[0].selectedRefinements).filter(
                                ([key]) => !HIDDEN_REFINEMENTS.includes(key)
                            )
                        )
                    })
                }
            }
        }
    )

    /**************** Structured Data ****************/
    const {breadcrumbListJSONLDScript} = useStructuredData(category)

    /**************** Error Handling ****************/
    const urlMappingErrorStatus = urlMappingError?.response?.status
    switch (urlMappingErrorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            throw new HTTPNotFound('Url Mapping Not Found.')
        default:
            throw new HTTPError(
                urlMappingErrorStatus,
                `HTTP Error ${urlMappingErrorStatus} occurred.`
            )
    }

    const categoryErrorStatus = categoryError?.response?.status
    switch (categoryErrorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            throw new HTTPNotFound('Category Not Found.')
        default:
            throw new HTTPError(categoryErrorStatus, `HTTP Error ${categoryErrorStatus} occurred.`)
    }

    /**************** Response Handling ****************/
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.plp ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /**************** Render Variables ****************/
    /**************** Action Handlers ****************/
    const convertHits = (hits) => {
        return (
            hits?.map((hit) => {
                const varAttributes = hit.variationAttributes
                // no need of overriding in case there are no variation attributes
                if (varAttributes?.[0]) {
                    varAttributes[0].values =
                        hit.c_variationAttributeValues || varAttributes[0].values
                }
                return {
                    ...hit,
                    ...hit.variants?.find((v) => v.productId === hit.productId),
                    id: hit.productId,
                    type: hit.productType,
                    name: hit.name || hit.c_productName || hit.productName,
                    imageGroups: hit.imageGroups || hit.c_imageGroups || [],
                    variants: hit.variants || hit.c_variants || [],
                    variationAttributes: varAttributes,
                    orderable: hit.productType !== 'master' ? hit.c_orderable : hit.orderable,
                    productPromotions: [{promotionalPrice: hit.c_promotionalPrice || null}],
                    validFrom: hit.validFrom || hit.c_validFrom,
                    brand: hit.brand || hit.c_brand || null
                }
            }) || []
        )
    }

    const basePath = `${location.pathname}${location.search}`

    const hits = useMemo(
        () => (productSearchResult?.total ? convertHits(productSearchResult.hits) : []),
        [productSearchResult]
    )

    const scrollFiltersSectionToView = () => {
        filtersRef?.current?.scrollIntoView?.()
    }

    useEffect(() => {
        if (forcelisting && window.type !== 'undefined') {
            scrollFiltersSectionToView()
            window.addEventListener('load', scrollFiltersSectionToView)
        }

        return () => {
            window.removeEventListener('load', scrollFiltersSectionToView)
        }
    }, [forcelisting])

    // If we are loaded and still have no products, show the no results component.
    const showNoResults = !isLoading && productSearchResult && !hits?.length

    if (productSearchResult?.searchPhraseSuggestions?.c_redirectURL) {
        let redirectURL = productSearchResult?.searchPhraseSuggestions?.c_redirectURL
        if (redirectURL.indexOf(locale.toLowerCase()) > 0) {
            redirectURL = redirectURL.substring(
                redirectURL.indexOf(locale.toLowerCase()) + locale.length
            )
        }
        navigate(redirectURL)
    }

    /**************** Tracking ****************/
    useInitialDataLayer({
        pageType: isSearch ? 'Search Result' : 'Product List',
        categoryId: isSearch ? '' : category?.id
    })
    useConvertPageTags(
        isSearch
            ? {
                  pageType: 'search result'
              }
            : {
                  pageType: 'product list',
                  categoryId: category?.id,
                  categoryName: category?.name
              }
    )

    const trackingLabel = isSearch ? 'search results' : category ? `PLP ${category.name}` : null
    useTrackViewList({
        products: hits,
        trackingLabel
    })

    const onProductSearchResultsChange = useEffectEvent((productSearchResult) =>
        isSearch
            ? einstein.sendViewSearch(searchQuery, productSearchResult)
            : einstein.sendViewCategory(category, productSearchResult)
    )
    useEffect(() => {
        if (productSearchResult) {
            onProductSearchResultsChange(productSearchResult)
        }
    }, [productSearchResult, onProductSearchResultsChange])

    /**************** Next page loading ****************/
    const [focusOnItemIndex, setFocusOnItemIndex] = useState(-1)
    const fetchNextPage = useCallback(() => {
        // set the focus on the first new item in the list when it loads
        setFocusOnItemIndex(hits.length)
        _fetchNextPage()
    }, [_fetchNextPage, hits.length])
    const focusSearchResult = useCallback((e) => {
        e?.focus?.()
        setFocusOnItemIndex(-1)
    }, [])

    /**************** Filters ****************/
    const hasSelectedRefinements = useMemo(
        () =>
            Object.entries(productSearchResult?.selectedRefinements || {}).some(
                ([key, value]) => !HIDDEN_REFINEMENTS.includes(key) && value
            ),
        [productSearchResult]
    )

    // Toggles filter on and off
    const toggleFilter = useCallback(
        (value, attributeId, selected, allowMultiple = true) => {
            const searchParamsCopy = {...searchParams}

            // Remove the `offset` search param if present.
            delete searchParamsCopy.offset

            // If we aren't allowing for multiple selections, simply clear any value set for the
            // attribute, and apply a new one if required.
            if (!allowMultiple) {
                const previousValue = searchParamsCopy.refine[attributeId]
                delete searchParamsCopy.refine[attributeId]

                // Note the loose comparison, for "string != number" checks.
                if (!selected && value.value != previousValue) {
                    searchParamsCopy.refine[attributeId] = value.value
                }
            } else {
                // Get the attribute value as an array.
                let attributeValue = searchParamsCopy.refine[attributeId] || []

                // Ensure that the value is still converted into an array if it's a `string` or `number`.
                if (typeof attributeValue === 'string') {
                    attributeValue = attributeValue.split('|')
                } else if (typeof attributeValue === 'number') {
                    attributeValue = [attributeValue]
                }

                // Either set the value, or filter the value out.
                if (!selected) {
                    attributeValue.push(value.value)
                } else {
                    // Note the loose comparison, for "string != number" checks.
                    attributeValue = attributeValue?.filter((v) => v != value.value)
                }

                // Update the attribute value in the new search params.
                searchParamsCopy.refine[attributeId] = attributeValue.join('|')

                // If the update value is an empty array, remove the current attribute key.
                if (searchParamsCopy.refine[attributeId].length === 0) {
                    delete searchParamsCopy.refine[attributeId]
                }
            }

            trackEvent({
                event: 'filter',
                content_type: isSearch ? 'search' : 'PLP',
                filter_action: 'apply_filter',
                filter: Object.entries(searchParamsCopy.refine).map(
                    ([filter_category, filter_id]) => ({
                        filter_category,
                        filter_id
                    })
                )
            })

            if (!isSearch) {
                navigate(
                    `/category/${params.categoryPath}?${stringifySearchParams(searchParamsCopy)}`
                )
            } else {
                navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
            }
        },
        [isSearch, navigate, params?.categoryPath, searchParams, stringifySearchParams]
    )

    // Clears all filters
    const resetFilters = useCallback(() => {
        trackEvent({
            event: 'filter',
            content_type: isSearch ? 'search' : 'PLP',
            filter_action: 'clear_all_filter'
        })

        if (isSearch) {
            if (searchQuery) {
                navigate(`/search?q=${searchQuery}`)
            } else {
                navigate(`/search`)
            }
        } else {
            navigate(getPathParamsSplit(window.location).pathname)
        }
    }, [isSearch, navigate, searchQuery])

    let selectedSortingOptionLabel = productSearchResult?.sortingOptions?.find(
        (option) => option.id === productSearchResult?.selectedSortingOption
    )

    // API does not always return a selected sorting order
    if (!selectedSortingOptionLabel) {
        selectedSortingOptionLabel = productSearchResult?.sortingOptions?.[0]
    }

    const onBackToTopLinkClick = () => {
        // focus first button of the page
        document.querySelectorAll('button')?.[0]?.focus()
    }

    const filtersProps = useMemo(
        () => ({
            basePath,
            hasSelectedRefinements,
            productSearchResult,
            onOpen,
            onResetFilters: resetFilters,
            onToggleFilter: toggleFilter,
            searchQuery
        }),
        [
            basePath,
            hasSelectedRefinements,
            productSearchResult,
            onOpen,
            resetFilters,
            toggleFilter,
            searchQuery
        ]
    )

    // redirect to slug url if it's different from the current url
    const redirectTo = useSlugUrlRedirect(isServer, category)
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    return (
        <Box className="sf-product-list-page" data-testid="sf-product-list-page">
            {isSearch ? (
                <Seo
                    title={formatMessage({
                        id: 'global.page.default_site_title',
                        defaultMessage: 'Children´s High Chair, Strollers & Nursery'
                    })}
                />
            ) : (
                <Seo
                    title={category?.pageTitle}
                    description={category?.pageDescription}
                    keywords={category?.pageKeywords}
                />
            )}
            <Helmet>
                {!isSearch && <meta property="evg:categoryId" content={category?.id} />}
                {!isSearch && <meta property="evg:categoryName" content={category?.name} />}
                {!isSearch && <meta property="evg:pageType" content={'category-page'} />}
                {isSearch ? (
                    <meta name="robots" content="noindex" />
                ) : (
                    <link rel="canonical" href={category?.c_canonicalUrl} />
                )}
                {breadcrumbListJSONLDScript}
            </Helmet>
            <span>
                {showNoResults ? (
                    <>
                        <HeaderContent />
                        <EmptyResults
                            isSearch={isSearch}
                            searchQuery={searchQuery}
                            suggestions={
                                productSearchResult?.searchPhraseSuggestions?.suggestedPhrases
                            }
                            category={category}
                            filtersRef={filtersRef}
                            filtersProps={filtersProps}
                            hasSelectedRefinements={hasSelectedRefinements}
                        />
                    </>
                ) : (
                    <>
                        {/* Breadcrumbs */}
                        {category && (
                            <CategoriesBreadcrumb categories={category?.parentCategoryTree} />
                        )}

                        <HeaderContent />

                        {/* Content Slot - Search result top banner */}
                        {searchQuery && (
                            <ContentSlot
                                slotId="search-result-banner-top"
                                showError={false}
                                variant="cms"
                                disableSkeleton={true}
                            />
                        )}

                        {/* Header for search */}
                        {searchQuery && (
                            <Container layerStyle="page">
                                <PageHeader
                                    searchQuery={searchQuery}
                                    productSearchResult={productSearchResult}
                                    isLoading={isLoading}
                                />
                            </Container>
                        )}

                        {/* Cat banner content slot */}
                        {category?.id && (
                            <ContentSlot
                                slotId={CATEGORY_BANNER_SLOT_ID}
                                context="category"
                                contextId={category.id}
                                showError={false}
                                variant="cms"
                            />
                        )}
                        {/* PLP Velocity Content slot */}
                        {category?.c_velocityAssetID && (
                            <CMSContent id={category?.c_velocityAssetID} showError={false} />
                        )}

                        {/* FiltersSection */}
                        {!category?.c_hideProductGrid && (
                            <>
                                <PlpSubnavigation filtersRef={filtersRef}>
                                    <FiltersSection {...filtersProps} />
                                </PlpSubnavigation>
                                <Container ref={filtersRef} layerStyle="page" variant="no-padding">
                                    <FiltersSection {...filtersProps} />
                                </Container>
                            </>
                        )}

                        {/* Body  */}
                        <Container layerStyle="page" py={4} px={{base: 2, md: 4, lg: 8}}>
                            <Flex direction="column" gap={6}>
                                <SimpleGrid
                                    columns={{base: 2, lg: 4}}
                                    spacingX={{base: 2, lg: 4}}
                                    spacingY={{base: 6, xl: 8}}
                                    gridAutoFlow="dense"
                                >
                                    {/* Category banners */}
                                    {category?.id && (
                                        <>
                                            <ContentSlot
                                                slotId={CATEGORY_BANNER_PROMO_TOP}
                                                context="category"
                                                contextId={category.id}
                                                showError={false}
                                                variant="cms"
                                                // display only when results are visible
                                                display={
                                                    isFetching || !productSearchResult
                                                        ? 'none'
                                                        : undefined
                                                }
                                                // position in the grid
                                                gridColumn={{base: '1/span 2', lg: '3/span 2'}}
                                                gridRow={{base: '2', lg: '1'}}
                                                // hide the loading skeleton
                                                disableSkeleton
                                            />
                                            <ContentSlot
                                                slotId={CATEGORY_BANNER_PROMO_MIDDLE}
                                                context="category"
                                                contextId={category.id}
                                                showError={false}
                                                variant="cms"
                                                // display only when results are visible
                                                display={
                                                    isLoading || !productSearchResult
                                                        ? 'none'
                                                        : undefined
                                                }
                                                // position in the grid
                                                gridColumn="1/span 2"
                                                gridRow={{base: '5', lg: '3'}}
                                                // hide the loading skeleton
                                                disableSkeleton
                                            />
                                        </>
                                    )}
                                    {!category?.c_hideProductGrid &&
                                        (isLoading || !productSearchResult
                                            ? new Array(searchParams.limit)
                                                  .fill(0)
                                                  .map((_value, index) => (
                                                      <ProductTileSkeleton key={index} />
                                                  ))
                                            : hits.map((productSearchItem, index) => {
                                                  return (
                                                      <ProductTile
                                                          data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                                          key={index}
                                                          ref={
                                                              index === focusOnItemIndex
                                                                  ? focusSearchResult
                                                                  : null
                                                          }
                                                          product={productSearchItem}
                                                          dynamicImageProps={{
                                                              widths: [
                                                                  '50vw',
                                                                  '50vw',
                                                                  '20vw',
                                                                  '20vw',
                                                                  '25vw'
                                                              ]
                                                          }}
                                                          propagateDisabled
                                                          variant="default"
                                                          onClick={() => {
                                                              trackSelectItem(
                                                                  productSearchItem,
                                                                  null,
                                                                  trackingLabel,
                                                                  null
                                                              )
                                                              return false
                                                          }}
                                                      />
                                                  )
                                              }))}
                                </SimpleGrid>
                            </Flex>
                        </Container>
                        {/* Footer */}
                        {!category?.c_hideProductGrid && (
                            <Container layerStyle="page" variant="no-padding">
                                <Stack
                                    spacing={3}
                                    alignItems="center"
                                    paddingTop={4}
                                    paddingBottom={{base: 8, lg: 10}}
                                >
                                    <PaginationLegend
                                        offset={productSearchResult?.offset}
                                        total={productSearchResult?.total}
                                        resultsLength={hits.length}
                                    />
                                    <Button
                                        variant="outline"
                                        width="15.25rem"
                                        onClick={() => fetchNextPage()}
                                        isDisabled={!hasNextPage || isFetchingNextPage}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Load more"
                                            id="global.loadmore"
                                        />
                                    </Button>
                                </Stack>
                            </Container>
                        )}
                        {!searchQuery && category?.id && (
                            <ContentSlot
                                slotId="plp-promo-bottom"
                                context="category"
                                contextId={category.id}
                                showError={false}
                                variant="cms"
                            />
                        )}

                        {/*  Recommendation  */}
                        <ModuleContainer
                            background="subtle"
                            spacing="recommender"
                            className="is-pdp-recommendation"
                        >
                            <RecommendedProducts
                                title={formatMessage({
                                    defaultMessage: 'Product Recommendations',
                                    id: 'product_detail.recommended_products.title.product_recommendations'
                                })}
                                recommender="products-in-all-categories"
                                trackingLabel="PLP recommendations"
                            />
                        </ModuleContainer>

                        {/* Content Slot - Search result bottom banner */}
                        {searchQuery && (
                            <ContentSlot
                                slotId="search-result-banner-bottom"
                                showError={false}
                                variant="cms"
                            />
                        )}

                        {/* Back to top */}
                        <Flex background="stokkeCore.white" paddingY={6} justifyContent="center">
                            <BackToTopLink
                                scrollToRef={filtersRef}
                                onClick={onBackToTopLinkClick}
                            />
                        </Flex>
                    </>
                )}
            </span>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                motionPreset="slideInBottom"
                scrollBehavior="inside"
                variant="plp"
                colorScheme=""
                finalFocusRef={filterModalReturnRef}
                returnFocusOnClose={true}
            >
                <ModalOverlay />
                <ModalContent containerProps={{overflow: 'clip'}}>
                    <ModalHeader>
                        <Text display={{lg: 'none'}} textStyle="Regular 2XLarge">
                            <FormattedMessage
                                defaultMessage="Filter & Sort"
                                id="global.filter_and_sort"
                            />
                        </Text>
                        <Text display={{base: 'none', lg: 'block'}} textStyle="Regular 2XLarge">
                            <FormattedMessage
                                display={{base: 'none', lg: 'block'}}
                                defaultMessage="Filter"
                                id="product_list.label.filter"
                            />
                        </Text>
                        <Text textStyle="Regular Medium" color="stokkeGray.accessibleText">
                            {formatMessage(
                                {
                                    id: 'product_list.total_text',
                                    defaultMessage:
                                        '{total} {total, plural, one {product} other {products}}'
                                },
                                {total: productSearchResult?.total}
                            )}
                        </Text>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {isLoading && <LoadingSpinner />}
                        <Refinements
                            basePath={basePath}
                            productSearchResult={productSearchResult}
                            toggleFilter={toggleFilter}
                            filters={productSearchResult?.refinements}
                            selectedFilters={productSearchResult?.selectedRefinements}
                            searchQuery={searchQuery}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Flex direction="column" align="center" gap="6">
                            <Button variant="link" onClick={() => resetFilters()}>
                                <FormattedMessage
                                    defaultMessage="Clear Filters"
                                    id="product_list.modal.button.clear_filters"
                                />
                            </Button>
                            <Button width="full" onClick={onClose}>
                                {formatMessage(
                                    {
                                        id: 'product_list.modal.button.view_items',
                                        defaultMessage: 'View {count} items'
                                    },
                                    {
                                        count: productSearchResult?.total
                                    }
                                )}
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}

ProductList.getTemplateName = () => 'product-list'

ProductList.shouldGetProps = () => false

ProductList.propTypes = {
    categoryId: PropTypes.string
}

export default ProductList
